import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../../components/ui/card';
import { ShieldCheck, KeyRound, Mail, Smartphone } from 'lucide-react';
import { meApi } from '../../api/endpoints/me';

export const Route = createFileRoute('/_authenticated/_layout/me/security/')({
    component: SecurityIndex,
});

export function SecurityIndex() {
    const { t } = useTranslation(['common']);
    // El MENÚ de seguridad gatea los accesos MFA por `allowedMethods` del backend (misma query key
    // que MfaPreferenceForm / MfaMethodChooser). Si el tenant tiene MFA off el backend devuelve
    // `allowedMethods: []` y NO se muestra ningún acceso MFA. `change-password` queda SIEMPRE fuera
    // del gating. Loading → no agregamos items MFA (sin flicker, NO fail-open). Error → fail-open
    // (mostramos los 3) para no bloquear la seguridad por un fallo transitorio del fetch.
    const optionsQ = useQuery({
        queryKey: ['me', 'security', 'mfa', 'options'],
        queryFn: () => meApi.getMfaOptions(),
    });

    const items: { to: string; label: string; Icon: typeof ShieldCheck }[] = [];

    const allowed = optionsQ.data?.allowedMethods ?? [];
    const failOpen = optionsQ.isError;
    const showMfaPreference = failOpen || allowed.length >= 1;
    const showTotpSetup = failOpen || allowed.includes('TOTP');
    const showEmailSetup = failOpen || allowed.includes('EMAIL_OTP');
    // DEC-RBAC-084: SMS NO entra en el fail-open (requiere teléfono; el backend solo lo lista si el
    // user lo tiene). Se muestra solo si el endpoint lo devolvió explícito.
    const showSmsSetup = allowed.includes('SMS_OTP');

    // Orden de render preservado: preference → totp-setup → email-setup → change-password.
    if (showMfaPreference) {
        items.push({
            to: '/me/security/mfa-preference',
            label: t('common:security.mfa.preference'),
            Icon: Smartphone,
        });
    }
    if (showTotpSetup) {
        items.push({
            to: '/me/security/mfa-totp-setup',
            label: t('common:security.mfa.totpSetup'),
            Icon: ShieldCheck,
        });
    }
    if (showEmailSetup) {
        items.push({
            to: '/me/security/mfa-email-setup',
            label: t('common:security.mfa.emailSetup'),
            Icon: Mail,
        });
    }
    if (showSmsSetup) {
        items.push({
            to: '/me/security/mfa-sms-setup',
            label: t('common:security.mfa.smsSetup'),
            Icon: Smartphone,
        });
    }
    items.push({
        to: '/me/security/change-password',
        label: t('common:security.changePassword'),
        Icon: KeyRound,
    });
    return (
        <div className="space-y-4">
            <h1 className="text-2xl font-semibold">{t('common:security.title')}</h1>
            <div className="grid gap-3 sm:grid-cols-2">
                {items.map(({ to, label, Icon }) => (
                    <Link key={to} to={to} className="block">
                        <Card className="p-4 flex items-center gap-3 hover:bg-muted transition">
                            <Icon className="size-5 text-brand-accent" />
                            <span>{label}</span>
                        </Card>
                    </Link>
                ))}
            </div>
        </div>
    );
}
